<template>
  <section id="project-analysis-text-main-themes">
    <div class="themes__header">
      <ul class="themes__header-labels main__search-labels">
        <transition name="fade">
          <li v-show="isSearchMode">
            <span
              class="main__highlight search"
              style="background-color: lightgreen"
              id="themes-search-keyword"
            >
              <VisibleText>Search</VisibleText>
            </span>
          </li>
        </transition>
      </ul>
      <div class="select-wrapper themes__sort-wrapper" style="display: none">
        <label for="theme-sort-by" class="themes__sort-label">
          <VisibleText>Sort by: </VisibleText>
        </label>
        <select class="select themes__sort-options" id="theme-sort-by" v-model.number="sortBy">
          <option value="-1" :data-parent="$options.name">
            {{ translate('Select a theme', $options.name) }}
          </option>
          <option
            :value="index"
            :data-parent="$options.name"
            :key="index"
            v-for="(theme, index) in sortedThemes"
            :disabled="textAnalysisObject.selectedThemeUUIDs.includes(theme._id)"
          >
            {{ theme.name }}
          </option>
        </select>
      </div>
    </div>
    <ResponseList
      ref="MainThemesResponseRef"
      class="themes__response-list"
      :textAnalysisObject="textAnalysisObject"
      :propReadOnly="propReadOnly"
      :client-question-id="convertId(textAnalysisObject.selectedTextQuestion._id)"
      :responses="sortedResponses"
      :includeSmartCoverage="includeSmartCoverage"
      :hidden-comments="textAnalysisObject.hiddenComments"
      :get-highlight-items-method="getHighlightItems"
      :datasetIDQuestions="datasetIDQuestions"
      :hasPreProcessedText="hasPreProcessedText"
      :sortedResponsesLength="sortedResponsesLength"
      :sortedThemes="sortedThemes"
      @updateExistingThemeTitle="updateExistingThemeTitle"
      @ban-response="banResponseItem"
      @toggle-response-hide="toggleResponseItemHide"
      @runSortAndSelect="$emit('runSortAndSelect')"
      @updateTextObject="$emit('updateTextObject', $event)"
      @selectThisResponse="selectThisResponse($event)"
      @updateTheme="$emit('updateTheme', $event)"
      @refreshTheme="refreshTheme($event)"
      @removeTheme="removeTheme($event)"
    />
  </section>
</template>

<script>
// Components
import ResponseList from './Components/ResponseList.vue';

// Mixins
import textAnalysisMixin from '../Mixins/textAnalysisMixin.js';

export default {
  name: 'ProjectTextAnalysisMainThemes',
  mixins: [textAnalysisMixin],
  components: {
    ResponseList,
  },
  props: {
    datasetIDQuestions: {
      default: () => [],
      type: Array,
    },
    highlightFilters: {
      type: Array,
      default: () => [],
    },
    textAnalysisObject: {
      type: Object,
      required: false,
      default: () => {},
    },
    sortedResponses: {
      type: Array,
      required: false,
      default: () => [],
    },
    hasPreProcessedText: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeSmartCoverage: {
      type: String,
      default: () => 'exclude',
    },
    sortedResponsesLength: {
      type: Number,
      required: false,
      default: () => 0,
    },
    sortedThemes: {
      type: Array,
      default: () => [],
    },
    propReadOnly: {
      default: () => false,
      type: Boolean,
    },
  },
  data() {
    return {
      sortBy: -1 /* sort by themes */,
    };
  },
  created() {
    this.getHighlightItems = this.getHighlightItems.bind(this);
  },
  computed: {
    themeName() {
      for (let i = 0; i < this.sortedThemes.length; i++) {
        if (this.textAnalysisObject.selectedThemeUUIDs.includes(this.sortedThemes[i]._id)) {
          return this.sortedThemes[i].name;
        }
      }
      return 'Theme';
    },
  },
  methods: {
    refreshThemesFromParent() {
      if (this.$refs.MainThemesResponseRef) {
        this.$refs.MainThemesResponseRef.refreshThemesFromParent();
      }
    },
    convertId(id) {
      if (id && id.$oid) {
        return id.$oid;
      } else {
        return id;
      }
    },
    updateTextObject(cleanedProject) {
      let object = {
        project: cleanedProject,
        scroll: 'main',
      };
      this.$emit('updateTextObject', object);
    },
    deepCloneObj(obj) {
      // deep clones an object using JSON stringify (data loss might occur)
      if (Array.isArray(obj)) {
        return obj.map((item) => JSON.parse(JSON.stringify(item)));
      } else if (typeof obj == 'object') {
        return JSON.parse(JSON.stringify(obj));
      }
    },
    findResponseScore(response) {
      if (!response.scores) {
        return 0;
      }
      if (response.scores.themes[this.sortBy]) {
        return response.scores.themes[this.sortBy];
      }
      for (let i = 0; i < this.sortedThemes.length; i++) {
        if (this.textAnalysisObject.selectedThemeUUIDs.includes(this.sortedThemes[i]._id)) {
          return response.scores.themes[i];
        }
      }

      return 0;
    },

    getHighlightItems(response) {
      if (!this.textAnalysisObject || !response || !response.id) return {};

      const isFiltered =
        this.textAnalysisObject.filterParams &&
        Object.keys(this.textAnalysisObject.filterParams).length;

      const covKey = isFiltered ? 'filtered_coverage' : 'coverage';

      const allKeywords = [];
      const allNotes = [];
      const allMatches = [];

      for (const theme of this.sortedThemes) {
        const { theme_color: color, name, _id } = theme;
        const cov = theme[covKey] || {};
        const respText = response.response.toLowerCase();

        let hasKeyword = false;
        let hasNote = false;

        // --- 1. Keyword match (highest UI priority) ---
        const keywords = cov.matching_keywords || [];
        if (keywords.some((k) => respText.includes(k.toLowerCase()))) {
          hasKeyword = true;
          allKeywords.push({
            type: 'keywords',
            color,
            matching_reasons: keywords,
            theme_name: name,
            theme_id: _id,
          });
        }

        // --- 2. Notes match (human override, second priority) ---
        const notes = (theme.notes || []).filter(
          (n) =>
            n.responseId == response.id || (response.old_id && n.responseId == response.old_id),
        );

        if (notes.length) {
          hasNote = true;
          allNotes.push({
            type: 'notes',
            color,
            matching_reasons: notes.map((n) => n.content),
            theme_name: name,
            theme_id: _id,
          });
        }

        // --- 3. Fallback: authoritative match only if no keyword or note ---
        if (!hasKeyword && !hasNote) {
          if (cov.matching_responses?.includes(response.id)) {
            allMatches.push({
              type: 'theme-match',
              color,
              matching_reasons: [],
              theme_name: name,
              theme_id: _id,
            });
          }
        }
      }

      // Build result object
      const result = {
        'theme-keyword': allKeywords,
        'theme-note': allNotes,
        'theme-match': allMatches,
      };

      // Apply highlight filters
      for (const key of Object.keys(result)) {
        if (!this.highlightFilters.includes(key)) delete result[key];
      }

      // Add search highlights
      const search = this.textAnalysisObject?.search;
      if (search?.searchResults?.length) {
        result['search-keyword'] = search.searchResults;
      }
      if (search?.similarSentences?.length) {
        result['search-keyword'] = search.similarSentences;
      }

      return result;
    },

    selectThisResponse(response_id) {
      if (response_id) {
        let these_responses = [];
        these_responses.push(response_id);
        this.setSelectedResponseIds(these_responses);
      }
    },
    refreshTheme(theme_id) {
      this.$emit('refreshTheme', theme_id);
    },
    async removeTheme(theme) {
      await this.$emit('removeTheme', theme);
    },
  },
  watch: {
    search: function (val) {
      const searchString = val.searchString;
      // reset sort
      if (searchString && searchString.trim() != '') this.sortBy = -1;
    },
  },
};
</script>
